import React, { useRef } from 'react';
import { FlaskConical, Syringe, Droplet, DownloadCloud } from 'lucide-react'; // Import Lucide icons
import { PEPTIDE_OPTIONS, PeptideName } from '../../constants/peptides';
import { Select } from '../Select';
import { Input } from '../Input';
import { CalculatorInputs } from '../../types/calculator';
import { Modal } from '../CalculatorModal/Modal';
import { VialEditor } from './Editors/VialEditor';
import { DosageEditor } from './Editors/DosageEditor';
import { SyringeEditor } from './Editors/SyringeEditor';
import { VialSvg } from '../Vial/Vial';
import { SyringeHorizontalViewOnly } from './Graphics/SyringeViewOnly';
import { Autocomplete } from '../Autocomplete';
import { LoadCalculateModal } from './LoadCalculateModal';
import { DOSAGE_UNITS, DosageUnit } from '../../types/data';

interface CalculateDosageFormProps {
  inputs: CalculatorInputs;
  errors: Partial<Record<keyof CalculatorInputs, string>>;
  onInputChange: (updates: Partial<CalculatorInputs>) => void;
  onCalculate: () => void;
  onReset: () => void;
}

export const CalculateDosageForm: React.FC<CalculateDosageFormProps> = ({
  inputs,
  errors,
  onInputChange,
  onCalculate,
  onReset,
}) => {
  const [openModal, setOpenModal] = React.useState<null | 'vial' | 'dosage' | 'syringe' | 'load'>(null);

  const handleCloseModal = () => setOpenModal(null);
  const vialSectionRef = useRef<HTMLDivElement | null>(null); // Ref for the vial section
  const scrollToVialSection = () => {
    if (vialSectionRef.current) {
      vialSectionRef.current.scrollIntoView({
        behavior: 'smooth', // Smooth scrolling
        block: 'start',
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">

      {/* Top Image Section */}
      <div className="mb-6">
        <img
          src="/images/peptides.jpg"
          alt="Peptides"
          className="w-full h-64 object-cover rounded-lg shadow-md"
        />
        <p className="text-sm text-gray-600 text-center mt-2">
          Calculate precise dosage metrics for your peptide vials
        </p>
        <button
          onClick={() => setOpenModal('load')}
          className="mt-4 flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors duration-200 mx-auto"
        >
          <DownloadCloud className="mr-2 w-5 h-5" />
          Load Sample Data
        </button>
      </div>

      <hr className="border-gray-300 my-2" />

      {/* Vial Section */}
      <div className="mb-6" ref={vialSectionRef}>
        <div className="flex items-center justify-center space-x-2">
          <FlaskConical className="text-indigo-500 w-6 h-6" />
          <h2 className="text-lg font-semibold">Vial</h2>
        </div>

        <div className="flex flex-wrap md:flex-nowrap mt-6">
          <div
            onClick={() => setOpenModal('vial')}
            className="w-full md:w-1/2 bg-gray-100 rounded-lg shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-200 mb-6 md:mb-0"
            style={{ minHeight: '180px' }}
          >
            <VialSvg
              label={inputs.peptideName}
              amount={inputs.vialAmount + ' ' + inputs.vialUnits}
              height={300}
              width={300}
            />
          </div>

          <div className="w-full md:w-1/2 md:ml-6">
            <div className="mb-4">
              <Autocomplete
                label="Substance Name"
                options={PEPTIDE_OPTIONS}
                onChange={(value) =>
                  onInputChange({ peptideName: value as PeptideName })
                }
                value={inputs.peptideName}
                error={errors.peptideName}
                required={true}
                tooltip="The name of the peptide, coenzyme, amino acid, etc."
                placeholder="Type to search or create new..."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Vial Amount"
                type="number"
                value={inputs.vialAmount}
                required={true}
                tooltip="Enter the total quantity of peptide or liquid in the vial."
                onChange={(value) => onInputChange({ vialAmount: value as number })}
                error={errors.vialAmount}
              />
              <Select
                label="Vial Units"
                value={inputs.vialUnits}
                required={true}
                tooltip="Select the measurement units for the vial's contents."
                onChange={(value) => onInputChange({ vialUnits: value as Unit })}
                options={DOSAGE_UNITS}
                error={errors.vialUnits}
              />
            </div>
          </div>
        </div>
      </div>

      <hr className="border-gray-300 my-2" />

      {/* Syringe Section */}
      <div className="mb-6">
        <div className="flex items-center justify-center space-x-2">
          <Syringe className="text-indigo-500 w-6 h-6" />
          <h2 className="text-lg font-semibold">Syringe</h2>
        </div>
        <div className="flex flex-wrap md:flex-nowrap mt-6">
          <div
            onClick={() => setOpenModal('syringe')}
            className="w-full md:w-1/2 bg-gray-100 rounded-lg shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-200 mb-6 md:mb-0"
            style={{ minHeight: '180px' }}
          >
            <SyringeHorizontalViewOnly
              syringeCapacity={Number(inputs.targetSyringeCapacity ?? '100')}
              syringeUnits={inputs.targetSyringeUnits}
            />
          </div>

          <div className="w-full md:w-1/2 md:ml-6">
            <Input
              label="Target Syringe Units to Draw"
              tooltip="Specify the total insulin syringe units you'd like to pull."
              value={inputs.targetSyringeUnits}
              required={true}
              onChange={(value) =>
                onInputChange({ targetSyringeUnits: value as number })
              }
              error={errors.targetSyringeUnits}
            />
          </div>
        </div>
      </div>

      <hr className="border-gray-300 my-2" />

      {/* Dosage Section */}
      <div className="mb-6">
        <div className="flex items-center justify-center space-x-2">
          <Droplet className="text-indigo-500 w-6 h-6" />
          <h2 className="text-lg font-semibold">Dosage</h2>
        </div>
        <div className="flex flex-wrap md:flex-nowrap mt-6">
          <div
            onClick={() => setOpenModal('dosage')}
            className="w-full md:w-1/2 bg-gray-100 rounded-lg shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-200 mb-6 md:mb-0"
            style={{ minHeight: '180px' }}
          >
            <p className="text-lg font-medium text-gray-600">Dosage</p>
          </div>

          <div className="w-full md:w-1/2 md:ml-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Dosage"
                value={inputs.dosage}
                required={true}
                tooltip="Enter the desired amount of peptide to administer per dose."
                onChange={(value) => onInputChange({ dosage: value as number })}
                error={errors.dosage}
              />
              <Select
                label="Dosage Units"
                value={inputs.dosageUnits}
                required={true}
                tooltip="Select the measurement units for your dosage."
                onChange={(value) => onInputChange({ dosageUnits: value as DosageUnit })}
                options={DOSAGE_UNITS}
                error={errors.dosageUnits}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end mt-4">
        <button
          onClick={onReset}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded mr-2"
        >
          Reset
        </button>
        <button
          onClick={onCalculate}
          className="bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded"
        >
          Calculate
        </button>
      </div>

      {/* Modals */}
      {openModal === 'vial' && (
        <Modal isOpen={true} onClose={handleCloseModal} title="Edit Vial">
          <VialEditor
            handleCloseModal={handleCloseModal}
            amount={inputs.vialAmount}
            units={inputs.vialUnits}
            name={inputs.peptideName}
            onChange={(amount: number, units: DosageUnit, name: PeptideName) =>
              onInputChange({ vialAmount: amount, vialUnits: units, peptideName: name })
            }
          />
        </Modal>
      )}

      {openModal === 'dosage' && (
        <Modal isOpen={true} onClose={handleCloseModal} title="Edit Dosage">
          <DosageEditor
            handleCloseModal={handleCloseModal}
            dosage={inputs.dosage}
            dosageUnits={inputs.dosageUnits}
            onChange={(dosage: number, units: DosageUnit) =>
              onInputChange({ dosage, dosageUnits: units })
            }
          />
        </Modal>
      )}

      {openModal === 'syringe' && (
        <Modal isOpen={true} onClose={handleCloseModal} title="Edit Syringe">
          <SyringeEditor
            syringeCapacity={inputs.targetSyringeCapacity}
            syringeUnits={inputs.targetSyringeUnits}
            handleCloseModal={handleCloseModal}
            onSyringeCapacityChange={(value: number) =>
              onInputChange({ targetSyringeCapacity: value })
            }
            onSyringeUnitsChange={(value: number) =>
              onInputChange({ targetSyringeUnits: value })
            }
          />
        </Modal>
      )}

      {openModal === 'load' && (
        <Modal isOpen={true} onClose={handleCloseModal} title="Load Data">
          <LoadCalculateModal
            closeModal={() => {
              setOpenModal(null);
            }}
            onInputChange={(props) => {
              onInputChange(props);
              scrollToVialSection();
            }}
          />
        </Modal>
      )}
    </div>
  );
};
